/**
 * Views of the accounts list: ready-made ones for the questions the team asks every week, and
 * views a person saves themselves (kept in this browser only: zInspector has nowhere to store
 * them). A view is just a list filter, so it can also be shared as a link.
 */
import type { Identifier } from "ra-core";

export interface AccountView {
  id: string;
  label: string;
  filter: Record<string, unknown>;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const STORAGE_KEY = "zcrm.accountViews";
const STALE_TOUCH_DAYS = 30;
const ONBOARDING_TARGET_DAYS = 30;

const daysAgo = (days: number, today: Date) =>
  new Date(today.getTime() - days * DAY_MS).toISOString().slice(0, 10);

export const presetAccountViews = (
  identityId?: Identifier,
  today = new Date(),
): AccountView[] => [
  { id: "clients", label: "All clients", filter: { view: "clients" } },
  {
    id: "mine",
    label: "My accounts",
    filter: identityId == null ? {} : { account_manager_id: identityId },
  },
  {
    id: "needs-info",
    label: "Clients missing key info",
    filter: { view: "clients", needs_info: true },
  },
  {
    id: "stale",
    label: `Clients not touched in ${STALE_TOUCH_DAYS} days`,
    filter: {
      view: "clients",
      "last_touch@lte": daysAgo(STALE_TOUCH_DAYS, today),
    },
  },
  {
    id: "stalled-onboarding",
    label: `Onboarding past day ${ONBOARDING_TARGET_DAYS}`,
    filter: {
      status: ["onboarding"],
      "days_since_signed@gte": ONBOARDING_TARGET_DAYS,
    },
  },
  {
    id: "worked-prospects",
    label: "Prospects being worked",
    filter: { view: "prospects", is_untouched: false },
  },
];

const read = (): AccountView[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AccountView[]) : [];
  } catch {
    return []; // Storage blocked or corrupted: no saved views.
  }
};

const write = (views: AccountView[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(views));
  } catch {
    // Storage blocked (private mode): the view lasts until the page closes.
  }
};

export const savedAccountViews = read;

export const saveAccountView = (
  label: string,
  filter: Record<string, unknown>,
): AccountView[] => {
  const views = [
    ...read().filter((v) => v.label !== label),
    { id: `saved-${Date.now()}`, label, filter },
  ];
  write(views);
  return views;
};

export const deleteAccountView = (id: string): AccountView[] => {
  const views = read().filter((v) => v.id !== id);
  write(views);
  return views;
};
